import { Flex, Spin } from '@brokerbay/elements-web';
import { ROLES } from '@bb/users';
import { AuthContext } from 'contexts';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import AppointmentBookingInternalContext from '../AppointmentBookingInternalContext';
import StepPanel from '../StepPanel';
import InPersonAlerts from './InPersonAlerts';
import { BookingTimeSelectContextContextProvider } from './BookingTimeSelect/BookingTimeSelectContext';
import DateSelectorStep from './BookingTimeSelect/DateSelectorStep';
import TimeSelectorStep from './BookingTimeSelect/TimeSelectorStep';
// @ts-expect-error angular component
import ListingPreview from '../../../../app/listing/preview/listing_preview.component';
import ComingSoonWarningModal from './BookingTimeSelect/ComingSoonWarningModal';
import RecordShowingModal from './BookingTimeSelect/RecordShowingModal';
import RestrictedTimeModal from './BookingTimeSelect/RestrictedTimeModal';
import CoopSelectorStep from './CoopSelect';
import { CoopSelectContextProvider } from './CoopSelect/CoopSelectContext';
import { SubmitAppointmentContextProvider } from './SubmitAppointmentContext';
import { getBookForOptions } from './helpers';

const InPersonTabLayout: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const {
    listing,
    offerDetails,
    isAdminWithAccess,
    hasElevatedAccess,
    setConditionalBuyerAgreementData,
    isLoading,
    appointmentConfig,
  } = useContext(AppointmentBookingInternalContext);
  const [isCoopSelected, setIsCoopSelected] = useState(false);

  const onCoopSelect = useCallback(() => setIsCoopSelected(true), []);

  useEffect(() => {
    setConditionalBuyerAgreementData({ coopSelected: isCoopSelected });
  }, [isCoopSelected, setConditionalBuyerAgreementData]);

  if (isLoading) {
    return (
      <StyledLoadingContainer justify="center" align="center">
        <Spin size="large" dataCy="appointmentBookinginPersonTab-spinner" />
      </StyledLoadingContainer>
    );
  }

  if (ROLES[auth.role] > ROLES.AGENT && !isCoopSelected) {
    return (
      <BookingTimeSelectContextContextProvider>
        <CoopSelectContextProvider onCoopSelect={onCoopSelect}>
          <StyledContainer $isAdmin>
            <CoopSelectorStep />

            {isAdminWithAccess && (
              <div className="global--angular">
                <ListingPreview
                  listingId={listing!.id}
                  listing={listing}
                  appointmentConfig={appointmentConfig!}
                  offerDetails={offerDetails}
                />
              </div>
            )}
          </StyledContainer>
        </CoopSelectContextProvider>
      </BookingTimeSelectContextContextProvider>
    );
  }

  const bookForOptions = getBookForOptions({
    auth,
    isAdminWithAccess,
    hasElevatedAccess,
  });
  const hasMultipleBookOptions = bookForOptions.length > 1;

  return (
    <BookingTimeSelectContextContextProvider>
      <CoopSelectContextProvider onCoopSelect={onCoopSelect}>
        <StyledContainer $isAdmin={false}>
          <InPersonAlerts />
          <SubmitAppointmentContextProvider />
          <StyledStepsContainer>
            {hasMultipleBookOptions && <CoopSelectorStep />}
            <StepPanel title="Select Date">
              <DateSelectorStep />
            </StepPanel>
            <StepPanel title="Select Time">
              <TimeSelectorStep />
            </StepPanel>
            {!hasMultipleBookOptions && <CoopSelectorStep />}
          </StyledStepsContainer>
        </StyledContainer>
      </CoopSelectContextProvider>
      <RecordShowingModal />
      <RestrictedTimeModal />
      <ComingSoonWarningModal />
    </BookingTimeSelectContextContextProvider>
  );
};

const StyledContainer = styled.div<{ $isAdmin: boolean }>`
  padding-top: 16px;
  ${({ $isAdmin }) =>
    $isAdmin &&
    `
    display: grid;
    grid-template-columns: minmax(350px, 1fr) minmax(600px, 2fr);
    grid-gap: 32px;
    `}
`;

const StyledStepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 32px;
`;

const StyledLoadingContainer = styled(Flex)`
  height: 60vh;
`;

export default InPersonTabLayout;
